import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import { RhssoAuthService } from '../services/rhsso-auth.service';
import { IdleTimeoutService } from '../services/idle-timeout.service';

const RETRY_HEADER = 'X-Token-Retry-Attempt';

/**
 * Interceptor funcional que anexa o Bearer token do usuário (RHSSO Authorization Code + PKCE)
 * em todas as requisições para as APIs backend.
 *
 * Se receber erro 401 (Unauthorized), tenta renovar o token automaticamente e retry a requisição.
 * Se o refresh falhar (refresh_token expirado no RHSSO), exibe modal informando timeout de sessão.
 * Ignora requisições ao próprio IAM.
 */

function handleRefreshTokenExpired(
  authService: RhssoAuthService,
  idleService: IdleTimeoutService,
  url: string
): Observable<never> {
  authService.clearSession();
  idleService.notifySessionExpired();
  return throwError(() => new HttpErrorResponse({ 
    status: 401, 
    statusText: 'Session Expired',
    url 
  }));
}

function retryWithNewToken(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: RhssoAuthService,
  originalError: HttpErrorResponse
): Observable<any> {
  return from(authService.getAccessToken()).pipe(
    switchMap((newToken) => {
      if (!newToken) {
        return throwError(() => originalError);
      }

      const retryReq = req.clone({
        setHeaders: { 
          Authorization: `Bearer ${newToken}`,
          [RETRY_HEADER]: 'true'
        }
      });
      
      return next(retryReq);
    })
  );
}

function handle401Error(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  error: HttpErrorResponse,
  authService: RhssoAuthService,
  idleService: IdleTimeoutService
): Observable<any> {
  if (error.status !== 401 || req.headers.has(RETRY_HEADER)) {
    return throwError(() => error);
  }

  return from(authService.refresh()).pipe(
    switchMap((refreshed: boolean) => {
      if (!refreshed) {
        authService.clearSession();
        idleService.notifySessionExpired();
        return throwError(() => error);
      }

      return retryWithNewToken(req, next, authService, error);
    }),
    catchError(() => {
      authService.clearSession();
      idleService.notifySessionExpired();
      return throwError(() => error);
    })
  );
}

function processRequestWithToken(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  token: string,
  authService: RhssoAuthService,
  idleService: IdleTimeoutService
): Observable<any> {
  const authReq = req.clone({
    setHeaders: { Authorization: `Bearer ${token}` }
  });

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => 
      handle401Error(req, next, error, authService, idleService)
    )
  );
}

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const rhssoAuthService = inject(RhssoAuthService);
  const idleTimeoutService = inject(IdleTimeoutService);

  // Ignora requisições ao próprio endpoint de token do RHSSO (login/refresh).
  // Comparar apenas com '/openid-connect/' falha quando iamTokenUrl está
  // configurado (ex.: prod, atrás de proxy em '/auth/user-token'), fazendo o
  // interceptor tratar a própria chamada de refresh como uma API protegida —
  // o que gera uma recursão (o refresh nunca completa porque fica esperando
  // um token que só ele mesmo produziria).
  if (req.url.includes('/openid-connect/') || rhssoAuthService.isTokenEndpoint(req.url)) {
    return next(req);
  }

  // Sessão já confirmada como expirada (modal em exibição): evita continuar
  // tentando getAccessToken()/refresh() a cada nova requisição enquanto o
  // usuário não confirma a modal (ex.: polling em segundo plano), o que
  // apenas repetiria indefinidamente uma falha já conhecida.
  if (idleTimeoutService.showTimeoutModal()) {
    return throwError(() => new HttpErrorResponse({
      status: 401,
      statusText: 'Session Expired',
      url: req.url
    }));
  }

  const hadRefreshToken = !!sessionStorage.getItem('rhsso_refresh_token');

  return from(rhssoAuthService.getAccessToken()).pipe(
    switchMap((token) => {
      if (!token) {
        if (hadRefreshToken) {
          return handleRefreshTokenExpired(rhssoAuthService, idleTimeoutService, req.url);
        }
        return next(req);
      }

      return processRequestWithToken(req, next, token, rhssoAuthService, idleTimeoutService);
    })
  );
};
